import React, { useState } from 'react';
import { Table, Button, Space, Tag, Tooltip, message, Switch } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import DocumentTypeModal from '../../../../components/modelpopup/DocumentTypeModal';
import {
  useGetDocumentTypesQuery,
  useSaveDocumentTypeMutation,
} from '../../../../services/settings.service';
import { DOCUMENT_TYPES } from '../../../../services/ENDPOINTS';
import { useDispatch } from 'react-redux';
import { toggleDelete } from '../../../../redux/slices/ui.slice';

const DocumentTypes = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDocType, setEditingDocType] = useState(null);
  const dispatch = useDispatch();

  // API Hooks
  const { data: docTypesData, isLoading, refetch } = useGetDocumentTypesQuery();
  const [saveDocType] = useSaveDocumentTypeMutation();

  const documentTypes = docTypesData?.data || [];

  // Handle Add
  const handleAdd = () => {
    setEditingDocType(null);
    setModalVisible(true);
  };

  // Handle Edit
  const handleEdit = (docType) => {
    setEditingDocType(docType);
    setModalVisible(true);
  };

  // Handle Delete
  const doDelete = (e, item) => {
    dispatch(
      toggleDelete({
        open: true,
        title: 'Document Type',
        url: `${DOCUMENT_TYPES}/${item.id}`,
        ele: e.currentTarget,
        parent: 'tr'
      })
    );
  };

  // Handle Active Toggle
  const handleActiveToggle = async (record) => {
    try {
      await saveDocType({
        ...record,
        isActive: !record.isActive,
      }).unwrap();
      message.success(`Document type ${record.isActive ? 'deactivated' : 'activated'} successfully`);
      refetch();
    } catch (error) {
      message.error('Failed to update status');
    }
  };

  // Table Columns
  const columns = [
    {
      title: 'Document Type',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            <FileTextOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            {text}
          </div>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
              {record.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Required',
      dataIndex: 'isRequired',
      key: 'isRequired',
      width: 120,
      render: (isRequired) => (
        <Tag color={isRequired ? 'error' : 'default'} icon={isRequired ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {isRequired ? 'Required' : 'Optional'}
        </Tag>
      ),
    },
    {
      title: 'Has Expiry',
      dataIndex: 'hasExpiry',
      key: 'hasExpiry',
      width: 120,
      render: (hasExpiry) => (
        <Tag color={hasExpiry ? 'warning' : 'default'}>
          {hasExpiry ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={() => handleActiveToggle(record)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => doDelete(e, record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Breadcrumb */}
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">Document Types</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/employee-dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Settings</li>
                </ul>
              </div>
              <div className="col-auto float-end ms-auto">
                <Link
                  to="#"
                  className="btn add-btn"
                  onClick={handleAdd}
                >
                  <i className="fa fa-plus" />
                  Add Document Type
                </Link>
              </div>
              
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="card mb-0">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col">
                      <h4 className="card-title mb-0">Document Types</h4>
                      <p className="text-muted mb-0">
                        Manage employee document types and requirements
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <Table
                    columns={columns}
                    dataSource={documentTypes}
                    rowKey="id"
                    loading={isLoading}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} document types`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <DocumentTypeModal
        visible={modalVisible}
        documentType={editingDocType}
        onClose={() => {
          setModalVisible(false);
          setEditingDocType(null);
        }}
        onSuccess={() => {
          setModalVisible(false);
          setEditingDocType(null);
          refetch();
        }}
      />
    </>
  );
};

export default DocumentTypes;
